import type {
  AssignmentLine,
  LineStockLocation,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  currentSource,
  matchesSourceSearch,
} from '../components/sourceOverride';
import { isQuantityEditable } from './lineQuantity';

/**
 * The locations the Elsewhere tab lists: by default only the
 * destination's division (Robert, 10/05: "it's showing me other divisions
 * here that I really don't need to see"), all of them on request. The
 * destination always stays.
 */
export function visibleStockLocations(
  locations: readonly LineStockLocation[],
  allDivisions: boolean,
): LineStockLocation[] {
  const destination = locations.find((location) => location.isDestination);
  const divisionId = destination?.divisionId ?? null;
  if (allDivisions || divisionId === null) return [...locations];
  return locations.filter(
    (location) => location.isDestination || location.divisionId === divisionId,
  );
}

/**
 * The rows for the pop-out's division toggle and search box (GEN-3147). With
 * nothing typed, the toggle decides as above. A search looks across every
 * division, since typing a code like "QE" or "QO HL" is the buyer asking for
 * those locations wherever they are. The destination always stays first.
 */
/**
 * The search as matching sees it: a `*` is dropped (see matchesSourceSearch),
 * so a wildcard-only entry is no search at all.
 */
export function normalizeStockSearch(search: string): string {
  return search.replaceAll('*', ' ').trim();
}

export function filterStockLocations(
  locations: readonly LineStockLocation[],
  { allDivisions, search }: { allDivisions: boolean; search: string },
): LineStockLocation[] {
  const normalized = normalizeStockSearch(search);
  if (normalized === '') return visibleStockLocations(locations, allDivisions);
  return locations.filter(
    (location) =>
      location.isDestination || matchesSourceSearch(location, normalized),
  );
}

/** What the pop-out offers on a location's row for the selected line. */
export type StockLocationAction = 'current-source' | 'transfer' | null;

type ActionLine = Pick<AssignmentLine, 'status' | 'topOption' | 'productV3Key'>;

/**
 * A location the line could be transferred from (GEN-3023's source switch):
 * not the destination, with stock available today, on a line whose source
 * the buyer can still change. The line's current transfer source is marked
 * instead.
 */
export function stockLocationAction(
  location: LineStockLocation,
  line: ActionLine | null,
): StockLocationAction {
  if (line === null || location.isDestination) return null;
  if (!isQuantityEditable(line) || line.productV3Key == null) return null;
  if (currentSource(line) === location.warehouseId) return 'current-source';
  return location.available > 0 ? 'transfer' : null;
}
